import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Avatar, Box, IconButton, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DataGridPro, GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import customLoadingTableOverlay from "../CustomLoadingTableOverlay";
import CustomNoRowsTableOverlay from "../CustomNoRowsTableOverlay";
import { BID_LINES_PAGE_TYPE } from "../../util/constants";
import CustomErrorTableOverlay from "../CustomErrorTableOverlay.jsx";
import { getShiftJobType } from "../../util/bid/shiftBidUtils";

const useStyles = makeStyles((theme) => ({
  bidDayColumn: {
    whiteSpace: "pre-wrap",
    textAlign: "left",
  },
  tableStyle: {
    width: "100%",
    height: "inherit",
  },
  mobileTableScroll: {
    width: "100%",
    height: `calc(100% - 5px)`,
    overflowX: "scroll",
  },
}));

const ShiftBidChoicesTable = (props) => {
  const classes = useStyles();
  const getValueBidLineDayCell = (row, dayOfWeek) => {
    const shift = row.shifts.find((shift) => shift.day === dayOfWeek);
    if (shift.isOff) {
      return "Off";
    } else {
      const shiftTime = `${shift.start}-${shift.end}`;
      const shiftJobType = getShiftJobType(shift, props.useAssignmentForEmp);
      return `${shiftTime} ${shiftJobType}`;
    }
  };

  const renderBidLineDayCell = (row, dayOfWeek) => {
    const shift = row.shifts.find((shift) => shift.day === dayOfWeek);
    if (shift.isOff) {
      return <Box className={classes.bidDayColumn}>Off</Box>;
    } else {
      const shiftTime = `${shift.start}-${shift.end}`;
      const shiftJobType = getShiftJobType(shift, props.useAssignmentForEmp);
      return (
        <Box className={classes.bidDayColumn}>
          <Typography sx={{ fontSize: "14px" }}>{shiftTime}</Typography>
          <Typography sx={{ fontSize: "10px" }}>{shiftJobType}</Typography>
        </Box>
      );
    }
  };

  const renderBidLineShiftDiffCell = (row) => {
    if (row.shiftDiff && row.shiftDiff !== "" && row.shiftDiff !== "0") {
      return (
        <Box className={classes.bidDayColumn}>
          <Typography sx={{ fontSize: "14px" }}>{row.lineNumber}</Typography>
          <Typography sx={{ fontSize: "10px", color: "primary.swaBlue" }}>${row.shiftDiff}</Typography>
        </Box>
      );
    } else {
      return row.lineNumber;
    }
  };

  const customAddRemoveIconPanel = (row) => {
    return props.selectedChoices?.includes(row.lineNumber) ? (
      <IconButton
        onClick={() => {
          handleRemoveChoice(row.lineNumber);
        }}
      >
        <RemoveIcon sx={{ color: "primary.swaBlue" }} />
      </IconButton>
    ) : (
      <IconButton
        onClick={() => {
          handleAddChoice(row.lineNumber);
        }}
      >
        <AddIcon sx={{ color: "white" }} />
      </IconButton>
    );
  };

  const getBlockLineNameRow = (params) => {
    return (
      params.row.isBlockLine && (
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "secondary.swaGray2",
            height: "100%",
            backgroundColor: "secondary.swaLightestBlue",
          }}
        >
          <Box
            sx={{
              display: "flex",
              bgcolor: "secondary.swaLightestBlue",
              height: "100%",
              paddingLeft: "10px",
              alignItems: "center",
              marginLeft: "7rem",
            }}
          >
            <Typography sx={{ fontSize: "12px" }}>
              Block Line (Bid Line {params.row.lineNumber}):{" "}
              <b>
                {params.row.assignedEmployee
                  ? `${params.row.assignedEmployee.firstName} ${params.row.assignedEmployee.lastName}`
                  : "Unassigned"}
              </b>
            </Typography>
          </Box>
        </Box>
      )
    );
  };

  const bidLineColumns = [
    {
      field: "choices",
      headerName: "Choice",
      renderCell: (params, idx) => {
        return (
          props.selectedChoices?.includes(params.row.lineNumber) && (
            <Box>
              <Avatar
                sx={{
                  bgcolor: "primary.swaBlue",
                  width: 32,
                  height: 32,
                }}
              >
                {findChoiceIndex(params.row.lineNumber)}
              </Avatar>
            </Box>
          )
        );
      },
      flex: 0.3,
      align: "center",
      cellClassName: (params) => {
        return clsx("choices", {
          selected: props.selectedChoices?.includes(params.row.lineNumber),
        });
      },
      sortable: false,
    },
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF, // Hide the default expand/collapse button
      renderCell: (params) => customAddRemoveIconPanel(params.row),
      width: "60",
      cellClassName: (params) => {
        return clsx("add-remove-icons", {
          addIcon: !props.selectedChoices?.includes(params.row.lineNumber),
        });
      },
    },
    {
      field: "lineNumberAndDifferential",
      headerName: "Bid Line",
      renderCell: (params, idx) => {
        return renderBidLineShiftDiffCell(params.row);
      },
      flex: 0.4,
      sortable: false,
    },
    {
      field: "Sunday",
      headerName: "Sun",
      renderCell: (params) => {
        return renderBidLineDayCell(params.row, "Sunday");
      },
      valueGetter: (params) => {
        return getValueBidLineDayCell(params.row, "Sunday");
      },
      flex: 0.75,
      sortable: false,
    },
    {
      field: "Monday",
      headerName: "Mon",
      renderCell: (params) => {
        return renderBidLineDayCell(params.row, "Monday");
      },
      valueGetter: (params) => {
        return getValueBidLineDayCell(params.row, "Monday");
      },
      flex: 0.75,
      sortable: false,
    },
    {
      field: "Tuesday",
      headerName: "Tue",
      renderCell: (params) => {
        return renderBidLineDayCell(params.row, "Tuesday");
      },
      valueGetter: (params) => {
        return getValueBidLineDayCell(params.row, "Tuesday");
      },
      flex: 0.75,
      sortable: false,
    },
    {
      field: "Wednesday",
      headerName: "Wed",
      renderCell: (params) => {
        return renderBidLineDayCell(params.row, "Wednesday");
      },
      valueGetter: (params) => {
        return getValueBidLineDayCell(params.row, "Wednesday");
      },
      flex: 0.75,
      sortable: false,
    },
    {
      field: "Thursday",
      headerName: "Thu",
      renderCell: (params) => {
        return renderBidLineDayCell(params.row, "Thursday");
      },
      valueGetter: (params) => {
        return getValueBidLineDayCell(params.row, "Thursday");
      },
      flex: 0.75,
      sortable: false,
    },
    {
      field: "Friday",
      headerName: "Fri",
      renderCell: (params) => {
        return renderBidLineDayCell(params.row, "Friday");
      },
      valueGetter: (params) => {
        return getValueBidLineDayCell(params.row, "Friday");
      },
      flex: 0.75,
      sortable: false,
    },
    {
      field: "Saturday",
      headerName: "Sat",
      renderCell: (params) => {
        return renderBidLineDayCell(params.row, "Saturday");
      },
      valueGetter: (params) => {
        return getValueBidLineDayCell(params.row, "Saturday");
      },
      flex: 0.75,
      sortable: false,
    },
  ];

  const bidLineMobileColumns = [
    {
      field: "choices",
      headerName: "Choice",
      renderCell: (params, idx) => {
        return (
          props.selectedChoices?.includes(params.row.lineNumber) && (
            <Box>
              <Avatar
                sx={{
                  bgcolor: "primary.swaBlue",
                  width: 32,
                  height: 32,
                }}
              >
                {findChoiceIndex(params.row.lineNumber)}
              </Avatar>
            </Box>
          )
        );
      },
      width: 56,
      align: "center",
      cellClassName: (params) => {
        return clsx("choices", {
          selected: props.selectedChoices?.includes(params.row.lineNumber),
        });
      },
      sortable: false,
    },
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF, // Hide the default expand/collapse button
      renderCell: (params) => customAddRemoveIconPanel(params.row),
      width: "60",
      cellClassName: (params) => {
        return clsx("add-remove-icons", {
          addIcon: !props.selectedChoices?.includes(params.row.lineNumber),
        });
      },
    },
    {
      field: "lineNumberAndDifferential",
      headerName: "Bid Line",
      renderCell: (params, idx) => {
        return renderBidLineShiftDiffCell(params.row);
      },
      width: "65",
      sortable: false,
    },
    {
      field: "Sunday",
      headerName: "Sun",
      renderCell: (params) => {
        return renderBidLineDayCell(params.row, "Sunday");
      },
      valueGetter: (params) => {
        return getValueBidLineDayCell(params.row, "Sunday");
      },
      width: "70",
      sortable: false,
    },
    {
      field: "Monday",
      headerName: "Mon",
      renderCell: (params) => {
        return renderBidLineDayCell(params.row, "Monday");
      },
      valueGetter: (params) => {
        return getValueBidLineDayCell(params.row, "Monday");
      },
      width: "70",
      sortable: false,
    },
    {
      field: "Tuesday",
      headerName: "Tue",
      renderCell: (params) => {
        return renderBidLineDayCell(params.row, "Tuesday");
      },
      valueGetter: (params) => {
        return getValueBidLineDayCell(params.row, "Tuesday");
      },
      width: "70",
      sortable: false,
    },
    {
      field: "Wednesday",
      headerName: "Wed",
      renderCell: (params) => {
        return renderBidLineDayCell(params.row, "Wednesday");
      },
      valueGetter: (params) => {
        return getValueBidLineDayCell(params.row, "Wednesday");
      },
      width: "70",
      sortable: false,
    },
    {
      field: "Thursday",
      headerName: "Thu",
      renderCell: (params) => {
        return renderBidLineDayCell(params.row, "Thursday");
      },
      valueGetter: (params) => {
        return getValueBidLineDayCell(params.row, "Thursday");
      },
      width: "70",
      sortable: false,
    },
    {
      field: "Friday",
      headerName: "Fri",
      renderCell: (params) => {
        return renderBidLineDayCell(params.row, "Friday");
      },
      valueGetter: (params) => {
        return getValueBidLineDayCell(params.row, "Friday");
      },
      width: "70",
      sortable: false,
    },
    {
      field: "Saturday",
      headerName: "Sat",
      renderCell: (params) => {
        return renderBidLineDayCell(params.row, "Saturday");
      },
      valueGetter: (params) => {
        return getValueBidLineDayCell(params.row, "Saturday");
      },
      width: "70",
      sortable: false,
    },
  ];
  const handleAddChoice = (bidLineNumber) => {
    let copiedSelectedChoices = [...props.selectedChoices];
    copiedSelectedChoices.push(bidLineNumber);
    props.setSelectedChoices(copiedSelectedChoices);
  };

  const handleRemoveChoice = (bidLineNumber) => {
    let copiedSelectedChoices = [...props.selectedChoices];
    copiedSelectedChoices = copiedSelectedChoices.filter((item) => item !== bidLineNumber);
    props.setSelectedChoices(copiedSelectedChoices);
  };

  const findChoiceIndex = (bidLine) => {
    return props.selectedChoices.indexOf(bidLine) + 1;
  };

  return (
    <div className={props.isDesktopView ? classes.tableStyle : classes.mobileTableScroll}>
      <DataGridPro
        columns={props.isDesktopView ? bidLineColumns : bidLineMobileColumns}
        rows={props.filteredBidLines}
        headerHeight={50}
        rowHeight={50}
        getRowHeight={() => "auto"}
        density="compact"
        components={{
          LoadingOverlay: customLoadingTableOverlay,
          NoRowsOverlay: CustomNoRowsTableOverlay,
          ErrorOverlay: CustomErrorTableOverlay,
          Toolbar: null,
        }}
        componentsProps={{
          noRowsOverlay: { pageType: BID_LINES_PAGE_TYPE },
        }}
        loading={props.isBidLinesLoading}
        disableColumnMenu={true}
        showColumnVerticalBorder={true}
        showCellVerticalBorder={true}
        showCellRightBorder={true}
        initialState={{
          pinnedColumns: {
            left: ["choices", "__detail_panel_toggle__", "lineNumberAndDifferential"],
          },
        }}
        sx={{
          border: 0,
          "& .MuiDataGrid-footerContainer": {
            display: "none",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "primary.swaBlue",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-columnHeader": {
            padding: "0 4px 0 4px",
          },
          "& .offCell": {
            backgroundColor: "secondary.swaGray2",
          },
          "& .blockedBidLine": {
            backgroundColor: "secondary.swaLightestBlue",
          },
        }}
        getCellClassName={(params) => {
          if (params.row.isBlockLine) {
            return "blockedBidLine";
          }
          if (params.value === "Off") {
            return "offCell";
          }
        }}
        getDetailPanelContent={(params) => getBlockLineNameRow(params)}
        getDetailPanelHeight={() => 25}
        detailPanelExpandedRowIds={props.filteredBidLines
          .filter((item) => item.isBlockLine)
          .map((item) => item.lineNumber)}
      />{" "}
    </div>
  );
};

ShiftBidChoicesTable.propTypes = {
  isDesktopView: PropTypes.bool.isRequired,
  filteredBidLines: PropTypes.array.isRequired,
  isBidLinesLoading: PropTypes.bool.isRequired,
  selectedChoices: PropTypes.array,
  setSelectedChoices: PropTypes.func.isRequired,
  useAssignmentForEmp: PropTypes.bool.isRequired,
};

export default ShiftBidChoicesTable;
